import type { DataModel, Entity, EntityField, EntityRelationship } from "../types";
import { asBoolean, asString, asStringArray } from "./parse-bullets-v3";
import { parseRisks } from "./parse-risks-v3";

// Coerce js-yaml output (the `data_model:` block under a data-storage
// container) into a structured DataModel. Lenient by design: this block is
// additive and optional, so malformed entries are dropped rather than thrown —
// a typo in one field never breaks the whole upload. An entity needs an `id`;
// a field needs a `name`; a relationship needs `from` and `to`. Anything else
// is skipped silently.

const CARDINALITY_VALUES: ReadonlySet<NonNullable<EntityRelationship["cardinality"]>> = new Set([
  "one-to-one",
  "one-to-many",
  "many-to-many",
]);

export function parseDataModel(value: unknown): DataModel | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const rec = value as Record<string, unknown>;

  const entities = coerceEntities(rec.entities);
  const relationships = coerceRelationships(rec.relationships);

  // A data model with no parseable entities is treated as absent — the L3
  // "Data Model" view only appears when there's something to draw.
  if (entities.length === 0) return undefined;

  return relationships.length > 0 ? { entities, relationships } : { entities };
}

function coerceEntities(value: unknown): Entity[] {
  if (!Array.isArray(value)) return [];
  const out: Entity[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const rec = raw as Record<string, unknown>;
    const id = asString(rec.id);
    if (!id) continue;
    const fields = coerceFields(rec.fields);
    const examples = asStringArray(rec.examples);
    const notable = asStringArray(rec.notable);
    const risks = parseRisks(rec.risks, { scope: `dataModel.entities.${id}`, strict: false });
    out.push({
      id,
      label: asString(rec.label) ?? id,
      description: asString(rec.description),
      why: asString(rec.why),
      examples: examples.length > 0 ? examples : undefined,
      risks: risks.length > 0 ? risks : undefined,
      notable: notable.length > 0 ? notable : undefined,
      fields: fields.length > 0 ? fields : undefined,
    });
  }
  return out;
}

function coerceFields(value: unknown): EntityField[] {
  if (!Array.isArray(value)) return [];
  const out: EntityField[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const rec = raw as Record<string, unknown>;
    const name = asString(rec.name);
    if (!name) continue;
    out.push({
      name,
      type: asString(rec.type),
      pk: asBoolean(rec.pk),
      fk: coerceFk(rec.fk),
      nullable: asBoolean(rec.nullable),
      description: asString(rec.description),
    });
  }
  return out;
}

function coerceFk(value: unknown): EntityField["fk"] {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const rec = value as Record<string, unknown>;
  const entity = asString(rec.entity);
  if (!entity) return undefined;
  const field = asString(rec.field);
  return field ? { entity, field } : { entity };
}

function coerceRelationships(value: unknown): EntityRelationship[] {
  if (!Array.isArray(value)) return [];
  const out: EntityRelationship[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const rec = raw as Record<string, unknown>;
    const from = asString(rec.from);
    const to = asString(rec.to);
    if (!from || !to) continue;
    const rawCardinality = asString(rec.cardinality)?.toLowerCase();
    const cardinality =
      rawCardinality && CARDINALITY_VALUES.has(rawCardinality as EntityRelationship["cardinality"])
        ? (rawCardinality as EntityRelationship["cardinality"])
        : undefined;
    out.push({
      from,
      to,
      label: asString(rec.label),
      cardinality,
    });
  }
  return out;
}
